import {CSSProperties, forwardRef, HTMLInputTypeAttribute, InputHTMLAttributes, ReactNode, useState} from 'react'
import {
    StyledInput,
    StyledInputText,
    StyledInputTextGroup,
    StyledInputWrapper,
    StyledVisibleToggle
} from '@components/commons/input-text/style.ts'
import {EyeIcon, EyeOffIcon} from '@components/ui/icon/Icon.tsx'
import {Label} from '@components/ui/label/Label.tsx'
import {InputStatusIcon} from '@components/ui/input-status-icon/InputStatusIcon.tsx'
import {InputHelpText} from '@components/ui/input-help-text/InputHelpText.tsx'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'

export interface InputTextProps extends InputHTMLAttributes<HTMLInputElement> {
    className?: string
    label?: ReactNode
    name?: string
    type: HTMLInputTypeAttribute
    typeIcon?: ReactNode
    touched?: boolean
    errorMessage?: string
    successMessage?: string
    visibilityToggle?: boolean
    helpText?: string
    placeholder?: string
    inputSize?: 'sm' | 'md' | 'lg'
    width?: CSSProperties['width']
    isOptional?: boolean
}

export const InputText = forwardRef<HTMLInputElement, InputTextProps>(
    (
        {
            label,
            name,
            type,
            typeIcon,
            touched = false,
            errorMessage,
            successMessage,
            helpText,
            className,
            disabled,
            placeholder,
            visibilityToggle = false,
            inputSize = 'md',
            width = '100%',
            isOptional = false,
            onKeyDown,
            onPaste,
            min,
            inputMode,
            step,
            ...rest
        },
        ref
    ) => {
        const {t} = useTranslation()
        const [eyeVisible, setEyeVisible] = useState(false)
        const isNumberInput = type == 'number'

        const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
            if (isNumberInput) {
                const blockedKeys = ['e', 'E', '+', '-']
                if (blockedKeys.includes(event.key)) {
                    event.preventDefault()
                    return
                }

                // Allow only a single decimal separator ('.' or ',')
                if (event.key == '.' || event.key == ',') {
                    const currentValue = event.currentTarget.value
                    if (currentValue.includes('.') || currentValue.includes(',')) {
                        event.preventDefault()
                        return
                    }
                }
            }

            onKeyDown?.(event)
        }

        const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
            if (isNumberInput) {
                const pastedText = event.clipboardData.getData('text')
                const normalized = pastedText.replace(',', '.')
                // Accept only positive decimal numbers
                if (!/^\d*(?:\.\d*)?$/.test(normalized)) {
                    event.preventDefault()
                    return
                }
            }
            onPaste?.(event)
        }

        return (
            <StyledInputText className={className} $width={width} direction="column" gap={1.5}>
                {label && (
                    <Label htmlFor={name}>
                        {label} {isOptional && <span className="description">({t('commons:optional')})</span>}
                    </Label>
                )}
                <StyledInputTextGroup align="center" fullWidth $hasError={!!errorMessage} $disabled={disabled}>
                    <StyledInputWrapper hasError={!!errorMessage} $size={inputSize} visibilityToggle={visibilityToggle}>
                        {typeIcon}
                        <StyledInput
                            ref={ref}
                            name={name}
                            type={eyeVisible ? 'text' : type}
                            placeholder={placeholder}
                            disabled={disabled}
                            // Rules added to handle number inputs
                            // Defaults for number inputs: positive numbers only and better mobile keyboard
                            min={isNumberInput ? min ?? 0 : min}
                            inputMode={isNumberInput ? inputMode ?? 'decimal' : inputMode}
                            step={step}
                            onKeyDown={handleKeyDown}
                            onPaste={handlePaste}
                            {...rest}
                        />
                    </StyledInputWrapper>

                    {/*input Action toggle*/}
                    {visibilityToggle && (
                        <StyledVisibleToggle
                            $size={inputSize}
                            $disabled={disabled}
                            onClick={() => setEyeVisible(!eyeVisible)}
                        >
                            {eyeVisible ? <EyeIcon size={20} data-color /> : <EyeOffIcon size={20} data-color />}
                        </StyledVisibleToggle>
                    )}
                </StyledInputTextGroup>
                {(helpText || errorMessage || successMessage) && (
                    <Flexbox align="center" gap={errorMessage || successMessage ? 1.5 : 0}>
                        {(successMessage || errorMessage) && (
                            <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                        )}
                        <InputHelpText error={errorMessage} success={successMessage} helpText={helpText} />
                    </Flexbox>
                )}
            </StyledInputText>
        )
    }
)

InputText.displayName = 'InputText'
